/**
 * Rendering an untyped value as text.
 *
 * Three screens need this and each needed it for the same reason: they display data whose
 * shape is not known at compile time. The audit trail's `old_values` / `new_values` are
 * `jsonb` (LV §102), a conflict comparison holds whatever the record's payload was
 * (Arch §15), and a work order's change list is the diff the backend produced.
 *
 * The rule these screens have to obey is that **every value must be readable**. An audit
 * entry rendering `[object Object]` is an audit entry that fails its purpose — LV §177's
 * acceptance test asks a person to see who changed what, and "[object Object]" answers
 * neither. So objects and arrays are rendered as JSON rather than stringified.
 */

/**
 * Format any value for display.
 *
 * @param value      The value, of unknown type.
 * @param absentText What to show for `undefined`. A missing key and a `null` value are
 *                   different facts in a diff — one side did not have the field at all —
 *                   so the caller can distinguish them.
 */
export function formatValue(value: unknown, absentText = "—"): string {
  if (value === undefined) return absentText;
  if (value === null) return "—";
  if (typeof value === "boolean") return value ? "ja" : "nein";
  if (typeof value === "string") return value === "" ? "—" : value;
  if (typeof value === "number" || typeof value === "bigint") return String(value);
  // Objects, arrays and anything else: JSON, so the content stays visible. JSON.stringify
  // returns undefined for a function or a symbol, which no payload should contain — the
  // fallback keeps the cell from rendering "undefined".
  return JSON.stringify(value) ?? "—";
}

/**
 * A `field: value` list, for the compact change summary on a detail page.
 *
 * `labels` is the audit row's server-resolved `{uuid: display name}` map. Without it
 * a change of responsibility reads `responsible_team_id: dd0a54a4-…`, which is a
 * correct record and an unusable screen. Optional, because the same helper formats
 * payloads that carry no ids.
 *
 * `coded` is the row's `enum_labels` — `{field: {code: German}}`. Without it the
 * change history on a work order reads `status: IN_PROGRESS`, which is the one English
 * string left on a German screen (LV §128, §161). Also optional, and a field it does
 * not cover keeps the stored code rather than being guessed at.
 */
export function summariseValues(
  value: Record<string, unknown> | null,
  labels: Record<string, string> = {},
  coded: Record<string, Record<string, string>> = {},
): string {
  if (!value || Object.keys(value).length === 0) return "—";
  return Object.entries(value)
    .map(([key, item]) => {
      const named =
        typeof item === "string" ? (labels[item] ?? coded[key]?.[item]) : undefined;
      return `${key}: ${named ?? formatValue(item)}`;
    })
    .join(", ");
}

/**
 * Sum decimal strings without ever making one a JS number.
 *
 * The API sends quantities and money as strings precisely so they do not become
 * floats (LV §117), and a total a technician checks against their own tally must not
 * drift by a rounding error. Summed as integer hundredths instead.
 *
 * Returns `null` for an empty list, which the formatters render as an em dash — a
 * total of "0,00" would claim something was booked.
 */
export function sumDecimals(values: string[]): string | null {
  if (values.length === 0) return null;
  const hundredths = values.reduce((total, value) => {
    const [whole = "0", fraction = ""] = value.split(".");
    const sign = whole.startsWith("-") ? -1 : 1;
    const magnitude =
      Math.abs(Number(whole || 0)) * 100 + Number(fraction.padEnd(2, "0").slice(0, 2));
    return total + sign * magnitude;
  }, 0);
  const sign = hundredths < 0 ? "-" : "";
  const absolute = Math.abs(hundredths);
  return `${sign}${Math.trunc(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`;
}
